import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Layers } from 'lucide-react';
import EntityLink from '../components/EntityLink';
import PosterPlaceholder from '../components/PosterPlaceholder';
import SiteHeader from '../components/SiteHeader';
import WatchlistButton from '../components/WatchlistButton';
import { titleTarget } from '../config/hubs';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// The native twin of /seo/franchise/{id} and /seo/genre/{slug}.
//
// Both are the same page — an entity, and its titles as poster cards — so like
// Hub they share one component. The only differences are the url parameter and
// the order the server returns titles in (release order for a franchise, most
// recently active for a genre), and the server owns that.
//
// A 404 here is meaningful, not an error: the API 404s a franchise with no
// indexable titles and 410s an adult-only one, matching /seo, so both render
// as "not found" rather than a retry prompt.
export default function HubDetail({ kind }) {
  const params = useParams();
  const key = params[kind.param];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setMissing(false);
    try {
      const r = await fetch(`${API_BASE}/hubs/${kind.slug}/${encodeURIComponent(key)}`);
      if (r.status === 404 || r.status === 410) {
        setMissing(true);
        setData(null);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(`Couldn't load this ${kind.noun}. Please try again.`);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [kind, key]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-gray-50">
      <SiteHeader />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link
          to={kind.backTo}
          className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> {kind.backLabel}
        </Link>

        {loading && <p className="text-gray-500">Loading…</p>}

        {!loading && error && (
          <div className="bg-white rounded-xl p-6 text-center">
            <p className="text-red-600 mb-3">{error}</p>
            <button
              onClick={load}
              className="px-4 py-2 bg-videbits-600 text-white rounded-lg hover:bg-videbits-700"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && missing && (
          <div className="bg-white rounded-xl p-6 text-center">
            <p className="text-gray-500">
              We don't have a page for this {kind.noun}.
            </p>
            <Link to={kind.backTo} className="text-sm text-videbits-600 hover:underline mt-2 inline-block">
              {kind.backLabel}
            </Link>
          </div>
        )}

        {!loading && !error && data && (
          <>
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <Layers className="w-6 h-6 text-videbits-600" />
                <h1 className="text-2xl font-bold text-gray-900">
                  {kind.heading(data.name)}
                </h1>
              </div>
              {kind.entityType && (
                <WatchlistButton entityType={kind.entityType} entityId={data.id} verb="follow" />
              )}
            </div>
            <p className="text-gray-600 mb-6">{kind.blurb(data.name)}</p>
            <p className="text-xs text-gray-400 mb-3">
              {data.count.toLocaleString()} title{data.count === 1 ? '' : 's'}
            </p>

            <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {data.results.map((t) => (
                <li key={t.id}>
                  <EntityLink target={titleTarget(t)} className="block group">
                    {t.poster_url ? (
                      <img
                        src={t.poster_url}
                        alt={t.title}
                        loading="lazy"
                        className="w-full aspect-[2/3] object-cover rounded-lg shadow-sm group-hover:shadow-md transition-shadow"
                      />
                    ) : (
                      <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-gray-100">
                        <PosterPlaceholder />
                      </div>
                    )}
                    <span className="block mt-2 text-sm text-gray-900 group-hover:text-videbits-600">
                      {t.title}
                    </span>
                  </EntityLink>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
